import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { fetchHealth } from '../lib/api';

/** Libellés lisibles des rôles renvoyés par l'API. */
const ROLE_LABELS: Record<string, string> = {
  platform_admin: 'Administration de la plateforme',
  tenant_admin: "Administration de l'établissement",
  instructor: 'Enseignant',
  assistant: 'Assistant',
  learner: 'Apprenant',
  member: 'Membre',
};

type Shortcut = {
  to: string;
  title: string;
  text: string;
  /** Rôles autorisés ; absent = visible par tous. */
  roles?: string[];
};

/**
 * Chaque carte pointe vers une page réelle. Les mentions « Disponible dans le
 * parcours EDUVERSA » dataient de C24, quand les portails n'existaient pas
 * encore : les afficher aujourd'hui laisse croire que la plateforme est vide.
 */
const SHORTCUTS: Shortcut[] = [
  { to: '/catalogue', title: 'Catalogue', text: 'Parcourir les matières et les programmes offerts.' },
  { to: '/learn', title: 'Mes cours', text: 'Reprendre là où vous vous êtes arrêté.' },
  { to: '/live', title: 'Classes en direct', text: 'Rejoindre une séance ou consulter le calendrier.' },
  { to: '/labs', title: 'Laboratoires', text: 'Expériences virtuelles et sessions guidées.' },
  { to: '/exams', title: 'Examens', text: 'Épreuves à venir et résultats.' },
  { to: '/credentials', title: 'Mes documents', text: 'Attestations, relevés et certificats.' },
  {
    to: '/users',
    title: 'Comptes',
    text: "Gérer les accès de l'établissement.",
    roles: ['platform_admin', 'tenant_admin'],
  },
  {
    to: '/operations',
    title: 'État système',
    text: 'Sessions, courriels sortants et tarifs.',
    roles: ['platform_admin'],
  },
];

export function DashboardPage() {
  const { principal } = useAuth();
  const [health, setHealth] = useState<{ status: string; version: string } | null>(null);

  useEffect(() => {
    fetchHealth()
      .then(setHealth)
      .catch(() => setHealth(null));
  }, []);

  const role = principal?.role ?? '';
  const roleLabel = ROLE_LABELS[role] ?? role;
  const visible = SHORTCUTS.filter((s) => !s.roles || s.roles.includes(role));

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">TABLEAU DE BORD</span>
          <h1>Bonjour</h1>
          {/* L'identifiant technique du tenant n'a rien à faire ici : il ne
              signifie rien pour un utilisateur. Le rôle, lui, est utile. */}
          <p>
            Vous êtes connecté à votre espace EDUVERSA{roleLabel ? ` — ${roleLabel}` : ''}.
          </p>
        </div>
        <span className={`status ${health ? 'ok' : 'warn'}`}>
          {health ? `API ${health.version}` : 'API indisponible'}
        </span>
      </header>

      <section className="hero-panel">
        <div>
          <span className="eyebrow">APPRENDRE • EXPÉRIMENTER • COLLABORER</span>
          <h2>Un seul espace pour vos cours, Lives et laboratoires.</h2>
          <p>
            Choisissez un espace ci-dessous, ou utilisez le menu latéral pour accéder à
            l’ensemble des rubriques.
          </p>
        </div>
      </section>

      <section className="card-grid">
        {visible.map((item) => (
          <Link className="card card-link" key={item.to} to={item.to}>
            <h3>{item.title}</h3>
            <p>{item.text}</p>
            <span>Ouvrir →</span>
          </Link>
        ))}
      </section>
    </div>
  );
}
